import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, extractError } from "@/lib/api";
import { useCurrency } from "@/lib/currency";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import MultiImageUpload from "@/components/MultiImageUpload";
import { Plus, Pencil, Trash2, MapPin, BedDouble, Bath, Users } from "lucide-react";

const empty = {
  agency_id: "", name: "", category: "apartment", description: "", photos: [],
  country: "", city: "", address: "", latitude: null, longitude: null,
  price_per_night: 0, currency: "XAF", min_nights: 1, max_guests: 2,
  bedrooms: 1, beds: 1, bathrooms: 1, amenities: [],
  house_rules: "", check_in_time: "14:00", check_out_time: "11:00",
  deposit_mode: "percent", deposit_fixed: 0, deposit_percent: 30,
  badges: [], instant_booking: false, active: true, conditions: "",
};

const CATEGORIES = [
  { key: "apartment", label: "Appartement" },
  { key: "villa", label: "Villa" },
  { key: "house", label: "Maison" },
  { key: "studio", label: "Studio" },
  { key: "hotel_room", label: "Chambre d'hôtel" },
];

const AMENITIES = [
  { key: "wifi", label: "Wi-Fi" },
  { key: "ac", label: "Climatisation" },
  { key: "kitchen", label: "Cuisine équipée" },
  { key: "pool", label: "Piscine" },
  { key: "parking", label: "Parking" },
  { key: "tv", label: "TV" },
  { key: "washer", label: "Lave-linge" },
  { key: "workspace", label: "Espace de travail" },
  { key: "breakfast", label: "Petit-déjeuner" },
  { key: "gym", label: "Salle de sport" },
  { key: "elevator", label: "Ascenseur" },
  { key: "security", label: "Sécurité 24/7" },
];

const BADGE_OPTIONS = [
  { key: "instant_booking", label: "Réservation instantanée" },
  { key: "insurance_included", label: "Assurance incluse" },
  { key: "premium", label: "Premium" },
];

export default function AdminLodgings() {
  const { fmt } = useCurrency();
  const [items, setItems] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/lodgings").then((r) => setItems(r.data));
  useEffect(() => { load(); api.get("/agencies").then((r) => setAgencies(r.data)); }, []);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleInArr = (k, val) => {
    const arr = form[k] || [];
    upd(k, arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val]);
  };

  const openNew = () => { setEditId(null); setForm(empty); setOpen(true); };
  const editIt = (v) => { setEditId(v.id); setForm({ ...empty, ...v }); setOpen(true); };

  const save = async () => {
    try {
      const payload = {
        ...form,
        price_per_night: parseFloat(form.price_per_night) || 0,
        min_nights: parseInt(form.min_nights) || 1,
        max_guests: parseInt(form.max_guests) || 1,
        bedrooms: parseInt(form.bedrooms) || 0,
        beds: parseInt(form.beds) || 0,
        bathrooms: parseInt(form.bathrooms) || 0,
        deposit_percent: parseFloat(form.deposit_percent) || 30,
        deposit_fixed: parseFloat(form.deposit_fixed) || 0,
      };
      if (editId) await api.put(`/lodgings/${editId}`, payload);
      else await api.post("/lodgings", payload);
      toast.success(editId ? "Logement mis à jour" : "Logement créé");
      setOpen(false); setForm(empty); setEditId(null); load();
    } catch (e) { toast.error(extractError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce logement ?")) return;
    await api.delete(`/lodgings/${id}`); load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl font-black text-[#0a1e3f]">Logements</h1>
          <p className="text-sm text-gray-500 mt-1">Facultatif — publiez ici les appartements, villas et maisons proposés par l'agence.</p>
        </div>
        <Button data-testid="add-lodging-btn" onClick={openNew} className="mbk-btn-green text-white rounded-full">
          <Plus className="h-4 w-4 mr-1" /> Nouveau logement
        </Button>
      </div>

      {items.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 border border-dashed border-gray-200 text-center">
          <div className="text-gray-500">Aucun logement pour l'instant.</div>
          <p className="text-xs text-gray-400 mt-2">Cette catégorie est optionnelle : n'ajoutez des logements que si votre agence en propose.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {items.map((l) => (
            <div key={l.id} data-testid={`lodging-${l.id}`} className="bg-white rounded-2xl border border-gray-100 mbk-card-shadow overflow-hidden">
              <div className="aspect-[4/3] bg-gray-100 overflow-hidden">
                {l.photos?.[0] ? <img src={l.photos[0]} alt={l.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-gray-300"><BedDouble className="h-12 w-12" /></div>}
              </div>
              <div className="p-5">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold text-[#0a1e3f] truncate">{l.name}</div>
                    <div className="text-xs text-gray-500 flex items-center gap-1"><MapPin className="h-3 w-3" /> {l.city}, {l.country}</div>
                  </div>
                  <span className="text-[10px] font-bold px-2 py-1 rounded-full bg-[#0e7a3f]/10 text-[#0e7a3f]">
                    {CATEGORIES.find((c) => c.key === l.category)?.label || l.category}
                  </span>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-gray-600">
                  <span className="inline-flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {l.max_guests}</span>
                  <span className="inline-flex items-center gap-1"><BedDouble className="h-3.5 w-3.5" /> {l.bedrooms} ch.</span>
                  <span className="inline-flex items-center gap-1"><Bath className="h-3.5 w-3.5" /> {l.bathrooms}</span>
                </div>
                <div className="mt-3 flex items-center justify-between">
                  <div className="font-display font-black text-[#0a1e3f]">{fmt(l.price_per_night)}<span className="text-xs font-normal text-gray-400"> /nuit</span></div>
                  <div className="flex gap-2">
                    <Button data-testid={`edit-lodging-${l.id}`} size="sm" variant="outline" onClick={() => editIt(l)}><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button data-testid={`del-lodging-${l.id}`} size="sm" variant="outline" onClick={() => remove(l.id)}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? "Modifier le logement" : "Nouveau logement"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <F l="Agence">
              <Select value={form.agency_id} onValueChange={(v) => upd("agency_id", v)}>
                <SelectTrigger data-testid="l-agency"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                <SelectContent>{agencies.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F l="Nom du logement"><Input data-testid="l-name" value={form.name} onChange={(e) => upd("name", e.target.value)} placeholder="Villa Ocean View" /></F>
            <F l="Catégorie">
              <Select value={form.category} onValueChange={(v) => upd("category", v)}>
                <SelectTrigger data-testid="l-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F l="Prix par nuit (XAF)"><Input data-testid="l-price" type="number" value={form.price_per_night} onChange={(e) => upd("price_per_night", e.target.value)} /></F>
            <F l="Pays"><Input value={form.country} onChange={(e) => upd("country", e.target.value)} /></F>
            <F l="Ville"><Input value={form.city} onChange={(e) => upd("city", e.target.value)} /></F>
            <div className="md:col-span-2"><F l="Adresse complète"><Input value={form.address} onChange={(e) => upd("address", e.target.value)} /></F></div>
            <F l="Capacité (voyageurs)"><Input type="number" min="1" value={form.max_guests} onChange={(e) => upd("max_guests", e.target.value)} /></F>
            <F l="Nombre de chambres"><Input type="number" min="0" value={form.bedrooms} onChange={(e) => upd("bedrooms", e.target.value)} /></F>
            <F l="Nombre de lits"><Input type="number" min="0" value={form.beds} onChange={(e) => upd("beds", e.target.value)} /></F>
            <F l="Nombre de salles de bain"><Input type="number" min="0" value={form.bathrooms} onChange={(e) => upd("bathrooms", e.target.value)} /></F>
            <F l="Nuits minimum"><Input type="number" min="1" value={form.min_nights} onChange={(e) => upd("min_nights", e.target.value)} /></F>
            <F l="Heure d'arrivée"><Input value={form.check_in_time} onChange={(e) => upd("check_in_time", e.target.value)} /></F>
            <F l="Heure de départ"><Input value={form.check_out_time} onChange={(e) => upd("check_out_time", e.target.value)} /></F>
            <div className="md:col-span-2"><F l="Description"><Textarea rows={3} value={form.description} onChange={(e) => upd("description", e.target.value)} /></F></div>
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-widest text-gray-500 font-bold">Photos</Label>
              <div className="mt-2"><MultiImageUpload value={form.photos} onChange={(v) => upd("photos", v)} /></div>
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-widest text-gray-500 font-bold">Équipements</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {AMENITIES.map((a) => {
                  const on = (form.amenities || []).includes(a.key);
                  return (
                    <button key={a.key} type="button" data-testid={`amenity-${a.key}`} onClick={() => toggleInArr("amenities", a.key)}
                      className={`text-xs font-semibold rounded-full px-3 py-1.5 border ${on ? "bg-[#0a1e3f] text-white border-[#0a1e3f]" : "bg-white text-gray-600 border-gray-200 hover:border-[#0a1e3f]"}`}>
                      {a.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-widest text-gray-500 font-bold">Badges</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {BADGE_OPTIONS.map((b) => {
                  const on = (form.badges || []).includes(b.key);
                  return (
                    <button key={b.key} type="button" onClick={() => toggleInArr("badges", b.key)}
                      className={`text-xs font-semibold rounded-full px-3 py-1.5 border ${on ? "bg-[#d4a437] text-[#0a1e3f] border-[#d4a437]" : "bg-white text-gray-600 border-gray-200"}`}>
                      {b.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="md:col-span-2 border-t pt-4">
              <div className="text-xs uppercase tracking-widest text-[#0e7a3f] font-bold mb-3">Acompte</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <F l="Mode">
                  <Select value={form.deposit_mode} onValueChange={(v) => upd("deposit_mode", v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percent">Pourcentage</SelectItem>
                      <SelectItem value="fixed">Montant fixe</SelectItem>
                    </SelectContent>
                  </Select>
                </F>
                {form.deposit_mode === "fixed"
                  ? <F l="Montant fixe (XAF)"><Input type="number" value={form.deposit_fixed} onChange={(e) => upd("deposit_fixed", e.target.value)} /></F>
                  : <F l="Pourcentage (%)"><Input type="number" value={form.deposit_percent} onChange={(e) => upd("deposit_percent", e.target.value)} /></F>}
                <div className="flex items-center gap-2 mt-6">
                  <Switch checked={form.instant_booking} onCheckedChange={(v) => upd("instant_booking", v)} />
                  <Label className="text-sm">Réservation instantanée</Label>
                </div>
              </div>
            </div>
            <div className="md:col-span-2"><F l="Règlement intérieur"><Textarea rows={3} value={form.house_rules} onChange={(e) => upd("house_rules", e.target.value)} placeholder="Non-fumeurs, pas d'animaux, silence entre 22h et 7h…" /></F></div>
          </div>
          <div className="mt-6 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button data-testid="save-lodging" onClick={save} className="mbk-btn-primary text-white">Enregistrer</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const F = ({ l, children }) => (<div><Label className="text-xs uppercase tracking-widest text-gray-500 font-bold">{l}</Label><div className="mt-1.5">{children}</div></div>);
